import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import RootState, { CountryOption } from "../../../../redux/type";
import { getNationalityCountries } from "../../../../redux/settings";

const EMPTY_COUNTRIES: CountryOption[] = [];

/**
 * Nationality countries (label = name, value = iso3_code) for the audience
 * "Country" filter rule. Pass `enabled = false` to skip fetching when no
 * country rule is in use.
 */
export function useNationalityCountries(enabled = true) {
  const dispatch = useDispatch();
  const { nationality_countries, nationality_countries_status } = useSelector(
    (state: RootState) => state.settings,
  );

  // Depends on the status too: other settings requests reset the whole slice
  // when they fail, which puts this list back to "idle" — re-fetch then.
  // The thunk's condition skips the request if it's loading or loaded.
  useEffect(() => {
    if (enabled && nationality_countries_status === "idle") {
      dispatch(getNationalityCountries() as any);
    }
  }, [enabled, nationality_countries_status, dispatch]);

  const retry = useCallback(() => {
    dispatch(getNationalityCountries() as any);
  }, [dispatch]);

  return {
    countries: nationality_countries || EMPTY_COUNTRIES,
    loading:
      enabled &&
      (nationality_countries_status === "loading" ||
        nationality_countries_status === "idle"),
    failed: nationality_countries_status === "failed",
    retry,
  };
}

/** Case-insensitive lookup of a saved iso3 code in the loaded list. */
export function findCountryOption(
  countries: CountryOption[],
  code: string,
): CountryOption | undefined {
  if (!code) return undefined;
  const key = code.trim().toUpperCase();
  return countries.find((country) => country.value.toUpperCase() === key);
}
